import { ColDef } from "ag-grid-community";
import { useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { formInEditModeState, holidaysState, useHolidayRow } from "../state";
import { SelectAllHeader } from "../utils";
import { useTranslation } from "react-i18next";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import { EditRounded } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import HolidayEditDialog from "../holiday-edit-dialog/HolidayEditDialog";

function TableCheckbox(props: any) {
    const row = useHolidayRow(props.data.id);

    return (
        <Checkbox
            onChange={(evt) => props.setValue(evt.target.checked)}
            checked={!!row?._select}
        />
    )
}

export default function useHolidayTableColumns() {
    const { t, i18n } = useTranslation(['network', 'translation']);
    const formInEditMode = useRecoilValue(formInEditModeState);

    const [rowData, setRowData] = useRecoilState(holidaysState);

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingRow, setEditingRow] = useState(-1);

    const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    };

    return useMemo<ColDef[]>(() => [
        {
            field: 'name',
            headerName: t("msHoliday.fields.holidayName"),
            flex: 1,
            minWidth: 200,
            editable: false,
            sortable: true,
            cellRenderer: (props: any) =>
                formInEditMode
                    ? rowData[props.node?.rowIndex]?.name
                    : props.value,
        },
        {
            field: '_date',
            headerName: t("msHoliday.fields.date"),
            flex: 1,
            minWidth: 200,
            editable: false,
            sortable: true,
            cellRenderer: (props: any) => {
                const dateToFormat = formInEditMode
                    ? rowData[props.node?.rowIndex]?._date
                    : props.value;

                if (dateToFormat && typeof dateToFormat.toLocaleDateString === 'function') {
                    return dateToFormat.toLocaleDateString(i18n.language, dateOptions);
                }

                return '';
            },
        },
        {
            field: 'tenant',
            headerName: t("msHoliday.fields.nation"),
            flex: 1,
            minWidth: 200,
            editable: false,
            cellRenderer: (props: any) =>
                formInEditMode
                    ? t(`msHoliday.legalEntities.${rowData[props.node?.rowIndex]?.tenant}`)
                    : t(`msHoliday.legalEntities.${props.value}`),
        },
        {
            field: '_select',
            headerName: t("msHoliday.fields.selectToAssociate"),
            pinned: 'right',
            minWidth: 180,
            editable: false,
            // Centre the header text; AG Grid exposes this utility class out of the box
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
            headerComponent: SelectAllHeader,
            headerComponentParams: {
                displayName: t("msHoliday.fields.selectToAssociate"),
                recoilState: holidaysState
            },
            cellRenderer: (props: any) => <TableCheckbox {...props} />,
        },
        ...(
            formInEditMode
                ? [{
                    field: '_actions',
                    headerName: '',
                    editable: false,
                    pinned: 'right' as const,
                    minWidth: 180,
                    cellRenderer: (props: any) => {
                        const rowIndex = props.node?.rowIndex;

                        function handleEditRow() {
                            setEditingRow(rowIndex);
                            setOpenEditDialog(true);
                        }

                        return (
                            <>
                                <Grid container alignContent={"center"}>
                                    <Stack mx="auto">
                                        <IconButton color={"accent"} onClick={handleEditRow}>
                                            <EditRounded/>
                                        </IconButton>
                                    </Stack>
                                </Grid>
                                <HolidayEditDialog
                                    title={t("msHoliday.labels.editHoliday")}
                                    open={openEditDialog && editingRow === rowIndex}
                                    rowIndex={rowIndex}
                                    onClose={() => setOpenEditDialog(false)}
                                />
                            </>
                        )
                    }
                }]
                : []
        ),
    ], [t, formInEditMode, rowData, openEditDialog])
}